import deepEquals from 'Lib/Object/deepEquals';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Subscription, fromEvent, interval } from 'rxjs';
import { filter, throttle } from 'rxjs/operators';
import { actions } from '../../../Data/ReduxViewModels/Notices';
import { AdditionalNotice } from './AdditionalNotice';
import Notice from './Notice';
import './NoticeManager.scss';

/**
 * A component used to manage the display of notice popups.
 *
 * - This manager ensures only a fixed number of notices are shown at any one time.
 * - A special AdditionalNotice is added at the end to identify the number of additional notices currently hidden
 * - The code is a bit messy to get animation right and has been directly ported from the scheduler.
 */
const NoticeManager: React.FC = () => {
  const dispatch = useDispatch();
  const MAX_NOTICES = 5; // NOTE: If this value changes, we must change some hard-coded values in Notice.scss
  const noticeQueue = useSelector((s) => s.notices.noticeQueue, deepEquals);
  const deleteNotice = useCallback((id: string) => dispatch(actions.deleteNotice({ id })), [dispatch]);
  const [keyDownSubscription, setKeyDownSubscription] = useState<Subscription>();
  const firstId = noticeQueue[0];

  useEffect(() => {
    // Set up key listener with throttling. Means you can hold escape and notices slowly disappear, not too quickly.
    const THROTTLE_TIME_MS = 100;

    const keyDown$ = fromEvent(document, 'keydown').pipe(
      filter((e: Event) => (e as KeyboardEvent)?.key === 'Escape' && !e.defaultPrevented),
      throttle(() => interval(THROTTLE_TIME_MS))
    );

    setKeyDownSubscription(
      keyDown$.subscribe((_) => {
        if (firstId) deleteNotice(firstId);
      })
    );
  }, [deleteNotice, firstId]);

  useEffect(() => {
    return () => {
      keyDownSubscription && keyDownSubscription.unsubscribe();
    };
  }, [keyDownSubscription]);

  let noticeComponents = noticeQueue.slice(0, MAX_NOTICES).map((id) => <Notice key={id} noticeId={id} />);

  if (noticeQueue.length > MAX_NOTICES) {
    noticeComponents.push(
      <AdditionalNotice additionalNoticeCount={noticeQueue.length - MAX_NOTICES} key="additional" />
    );
  }

  return (
    <div className="NoticeManager">
      {
        <TransitionGroup>
          {noticeComponents.map((notice, i) => (
            <CSSTransition classNames="Notice" timeout={300} appear={true} key={i}>
              {notice}
            </CSSTransition>
          ))}
        </TransitionGroup>
      }
    </div>
  );
};

export { NoticeManager };
export default NoticeManager;
